import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {getReport, ReportFilterValues, ReportPayload} from '@app/services/reports';
import {getAllActiveFranchise} from '@app/services/franchise';
import KpiStrip from './components/KpiStrip';
import ReportFilters, {FranchiseOption} from './components/ReportFilters';
import {
  ChartCard,
  ReportDonutChart,
  ReportMultiSeriesChart
} from './components/ReportChart';
import useReportExport from './hooks/useReportExport';
import {reportTableStyles} from './components/tableStyles';
import {humanise} from './components/labels';
import {findReportBySlug} from './reportCatalogue';

/**
 * ORD3 — Student Work Orders & Set Consumption.
 *
 * The consumption chart is the point: sets stacked used-against-remaining per
 * subject, so a thin remaining band means a reorder wave is coming.
 *
 * "Pending" is shown as a plain count, not called a backlog — there is no
 * completed state anywhere in the data, so what pending means is unconfirmed.
 */

const SLUG = 'student-work-orders';
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({length: 5}, (_, i) => CURRENT_YEAR - i);

const number = (v: any) => new Intl.NumberFormat('en-GB').format(Number(v) || 0);

const STATUS_BADGE: Record<string, string> = {
  pending: 'warning',
  on_hold: 'secondary',
  added_to_cart: 'info',
  exclude_forever: 'danger'
};

const StudentWorkOrders = () => {
  const definition = findReportBySlug(SLUG);

  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [franchises, setFranchises] = useState<FranchiseOption[]>([]);

  const [filters, setFilters] = useState<ReportFilterValues>({
    mode: 'year',
    year: CURRENT_YEAR,
    period: 'full',
    page: 1,
    limit: 25
  });
  const [applied, setApplied] = useState<ReportFilterValues>(filters);

  const {exportToExcel, exporting} = useReportExport('Student Work Orders');

  const load = useCallback(async (active: ReportFilterValues) => {
    setLoading(true);
    setError(null);
    const response = await getReport(SLUG, active);
    if (response && response.success) {
      setPayload(response);
    } else {
      setPayload(null);
      setError(response?.message || 'The report could not be loaded.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  useEffect(() => {
    (async () => {
      const response = await getAllActiveFranchise();
      if (response?.success === true && Array.isArray(response.collections)) {
        setFranchises(
          response.collections.map((f: any) => ({value: f.id, label: f.name}))
        );
      }
    })();
  }, []);

  const collections = payload?.collections;
  const byStatus = collections?.series?.by_status || [];
  const bySubject = collections?.series?.by_subject || [];
  const detail = collections?.detail;

  const columns = [
    {name: 'Student', selector: (row: any) => row.student || '---', wrap: true},
    {name: 'Franchise', selector: (row: any) => row.franchise || '---', wrap: true},
    {name: 'Subject', selector: (row: any) => row.subject || '---', width: '130px'},
    {
      name: 'Status',
      width: '150px',
      cell: (row: any) => (
        <span className={`badge badge-${STATUS_BADGE[row.status] || 'secondary'}`}>
          {humanise(row.status)}
        </span>
      )
    },
    {name: 'Raised', selector: (row: any) => row.raised_on || '---', width: '115px'},
    {name: 'Sets', width: '90px', right: true, selector: (row: any) => number(row.sets)},
    {name: 'Used', width: '100px', right: true, selector: (row: any) => number(row.used)},
    {
      name: 'Remaining',
      width: '120px',
      right: true,
      cell: (row: any) => (
        <span className={row.remaining === 0 ? 'text-danger font-weight-bold' : ''}>
          {number(row.remaining)}
        </span>
      )
    }
  ];

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-7">
              <h1>{definition?.name || 'Student Work Orders'}</h1>
              <p className="text-muted mb-0">{definition?.question}</p>
            </div>
            <div className="col-sm-5">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><Link to="/reports">Reports</Link></li>
                <li className="breadcrumb-item active">Student Work Orders</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <ReportFilters
            values={filters}
            onChange={setFilters}
            onApply={() => setApplied({...filters, limit: applied.limit, page: 1})}
            onReset={(defaults) => {
              setFilters(defaults);
              setApplied(defaults);
            }}
            onExport={() => exportToExcel(payload, SLUG, applied)}
            exporting={exporting}
            franchises={franchises}
            years={YEARS}
            intervalLabel={collections?.filters_applied?.interval_label}
            show={{franchise: true, subject: true, student: true, workOrderStatus: true}}
            disabled={loading}
          />

          {error && (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle mr-2" />
              {error}
            </div>
          )}

          <KpiStrip kpis={collections?.kpis || []} loading={loading} />

          <div className="row">
            <div className="col-lg-7">
              <ChartCard
                title="How used-up sets are, by subject"
                subtitle="A thin remaining band means a reorder wave is due"
                loading={loading}
                isEmpty={!bySubject.length}>
                <ReportMultiSeriesChart
                  data={bySubject}
                  xKey="subject"
                  currency={false}
                  series={[
                    {key: 'used', name: 'Worksheets used', stackId: 'w', colour: '#0F8F82'},
                    {key: 'remaining', name: 'Remaining', stackId: 'w', colour: '#C9873A'}
                  ]}
                />
              </ChartCard>
            </div>
            <div className="col-lg-5">
              <ChartCard
                title="Work orders by status"
                subtitle="No completed state exists in the data — see the Pending note"
                loading={loading}
                isEmpty={!byStatus.length}>
                <ReportDonutChart
                  data={byStatus}
                  nameKey="status"
                  valueKey="work_orders"
                />
              </ChartCard>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="card-title text-capitalize">Work orders</h3>
            </div>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                customStyles={reportTableStyles}
                data={detail?.rows || []}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={detail?.total || 0}
                paginationDefaultPage={applied.page || 1}
                paginationPerPage={applied.limit || 25}
                paginationRowsPerPageOptions={[25, 50, 100]}
                onChangePage={(page) => setApplied({...applied, page})}
                onChangeRowsPerPage={(limit, page) =>
                  setApplied({...applied, limit, page})
                }
                noDataComponent={
                  <div className="text-muted py-4">No work orders match these filters.</div>
                }
                highlightOnHover
                dense
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StudentWorkOrders;
